import createStyles from "../../pages/onboarding/CreateApplicationStep.module.css";
import styles from "./SharedWithLandlordScreen.module.css";

export type SharedMember = {
  name: string;
  email: string;
  isLead: boolean;
};

type SharedWithLandlordScreenProps = {
  names: string;
  appTitle: string | null;
  members: SharedMember[];
  userEmail: string;
  onDashboard: () => void;
  _t: (key: string, context?: string, ns?: string, args?: any) => string;
  // Individual-profile shares (no application): keeps the "{landlord} can now
  // review {tenant}'s application." line instead of the property line.
  tenantLabel?: string | null;
  // Hidden for individual shares (no other members to email about).
  showBellLine?: boolean;
};

// Shared-with-landlord success for application shares: confirms which
// landlord can now review the property, lists every application member
// (lead first, you second) and what the landlord sees. Individual-profile
// shares (no application) keep the older simple layout in CreateApplicationStep.
const SharedWithLandlordScreen = ({
  names,
  appTitle,
  members,
  userEmail,
  onDashboard,
  _t,
  tenantLabel = null,
  showBellLine = true,
}: SharedWithLandlordScreenProps) => {
  const me = (userEmail ?? "").toLowerCase();
  const firstName = names.split(",")[0]?.trim() || names;

  return (
    <div className={`${createStyles.overlay} modal-fixed-screen`}>
      <main
        className={`${createStyles.card} ${styles.shell} onboarding-modal-surface`}
      >
        <div className={styles.checkWrap} aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="m5 12.5 4.5 4.5L19 7.5" />
          </svg>
        </div>
        <div className={styles.heading}>
          <h1>
            {_t("sharedWithLandlord", "Shared with Landlord")} {names}
          </h1>
          <p className={createStyles.intro}>
            {tenantLabel ? (
              <>
                {firstName} {_t("canNowReview", "can now review")} {tenantLabel}
                {_t("applicationPossessive", "'s application")}.
              </>
            ) : (
              _t(
                "sharedReviewProperty",
                "Landlord {{landlordName}} can now review {{applicationName}}",
                undefined,
                { landlordName: names, applicationName: appTitle ?? "" }
              )
            )}
          </p>
        </div>

        <section
          className={`${createStyles.people} ${styles.members}`}
          aria-label="shared with"
        >
          {members.map((member, index) => {
            const isYou = me !== "" && member.email.toLowerCase() === me;
            const display = isYou ? _t("you", "You") : member.name;
            return (
              <div
                className={`${createStyles.personRow} ${styles.memberRow}`}
                key={member.email || index}
              >
                <span className={createStyles.avatar}>
                  {(display || "S").charAt(0).toUpperCase()}
                </span>
                <div>
                  <div className={createStyles.personName}>
                    <strong>{display}</strong>
                    <span>
                      {member.isLead
                        ? _t("leadTenant", "Lead tenant")
                        : _t("flatmate", "Flatmate")}
                    </span>
                  </div>
                  <p>{member.email}</p>
                </div>
                <span className={createStyles.sharedChip}>
                  {_t("shared", "Shared")}
                </span>
              </div>
            );
          })}
        </section>

        <div className={createStyles.seesBox}>
          <p className={createStyles.seesLabel}>
            {_t("whatLandlordSees", "WHAT {name} SEES", undefined, {
              name: names.toUpperCase(),
            })}
          </p>
          <p className={createStyles.seesText}>
            {_t(
              "landlordSeesFullApp",
              "The application with every member. They can generate the report."
            )}
          </p>
        </div>

        {showBellLine && (
          <p className={styles.bellLine}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 9.5a6 6 0 0 1 12 0c0 4 1.5 5.5 1.5 5.5h-15C4.5 15 6 13.5 6 9.5" />
              <path d="M10 19a2.2 2.2 0 0 0 4 0" />
            </svg>
            <span>
              {_t(
                "everyMemberGetsEmail",
                "Every member gets an email about this share."
              )}
            </span>
          </p>
        )}

        <button
          type="button"
          className={styles.dashboardButton}
          onClick={onDashboard}
        >
          {_t("goToMyDashboard", "Go to my dashboard")}
        </button>
      </main>
    </div>
  );
};

export default SharedWithLandlordScreen;
